import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { NumberInputRange } from '~src/settings/general-settings/utils';

import { Messages } from '../../../../../codegen/Localize';
import { FeatureFlags } from '../../../../../ts/FeatureFlags';
import { fetch,ILicensingInfoWithTotals } from '../../../../../ts/services/LicensesService';
import { disabledText, settingsRadioButtonItemHeight } from '../../../../styles/CommonStyles';
import { useSharedWidgetChangeHandler } from '../../../utils';
import { ManageUsersSectionProps } from './ManageUsersSection';
import { ManageUsersServerLimitOption } from './ManageUsersServerLimitOption';
import { CREATOR_QUOTA_FIELD, EXPLORER_QUOTA_FIELD, ManageUsersSiteRoleQuotaOption, VIEWER_QUOTA_FIELD } from './ManageUsersSiteRoleQuotaOption';
import { ManageUsersSiteUserQuotaOption, USER_QUOTA_FIELD } from './ManageUsersSiteUserQuotaOption';

export const USER_QUOTA_MODE_FIELD = 'settings.userQuotaMode';
export function ManageUsersAdminOption(props: ManageUsersSectionProps) {
  const formik = useFormikContext<ManageUsersSectionProps>();
  const disabled = ! formik.values.settings.siteAdminsCanAddUsers;

  const licenseMaxCounts = useLicensingInfoInfo();

  const buttons = React.useMemo(() => getButtonsList({
    ...props,
    maxCounts: licenseMaxCounts
  }, disabled), [props, licenseMaxCounts, disabled]);

  const index = buttons.findIndex(button => button.value === formik.values.settings.userQuotaMode);

  const handleChange = useSharedWidgetChangeHandler<string>(USER_QUOTA_MODE_FIELD);
  const mounted = React.useRef(false);

  React.useEffect(
    () => {
      const updateFieldValues = () => {
        if (formik.values.settings.userQuotaMode === 'site-tiered-capacity') {
          formik.setFieldValue(USER_QUOTA_FIELD, null);
        } else if (formik.values.settings.userQuotaMode === 'server-limit') {
          formik.setFieldValue(USER_QUOTA_FIELD, null);
          formik.setFieldValue(CREATOR_QUOTA_FIELD, null);
          formik.setFieldValue(EXPLORER_QUOTA_FIELD, null);
          formik.setFieldValue(VIEWER_QUOTA_FIELD, null);
        } else {
          formik.setFieldValue(CREATOR_QUOTA_FIELD, null);
          formik.setFieldValue(EXPLORER_QUOTA_FIELD, null);
          formik.setFieldValue(VIEWER_QUOTA_FIELD, null);
        }
      };
      updateFieldValues();
    },
    // This noncompliance existed before the lint rule was enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.settings.userQuotaMode, formik.values.settings.userQuota,
    formik.values.settings.tierCreatorCapacity, formik.values.settings.tierExplorerCapacity,
    formik.values.settings.tierViewerCapacity]
  );
  return <>
    <div>{Messages.serverAndSiteAdmins()}</div>
    <div className={props.settings.siteAdminsCanAddUsers ? undefined : disabledText}>{Messages.usersDetails()}</div>
    <RadioButtonWithLabelGroupWidget buttonList={buttons}
                                     aria-label={Messages.managingUsersDetails()}
                                     className={settingsRadioButtonItemHeight}
                                     checkedIndex={index}
                                     handleChange={handleChange}/>
  </>;
}

function getButtonsList(props: ManageUsersSectionProps & { maxCounts: LicenseMaxCounts }, disabled: boolean): RadioButtonWithLabelOption<string>[] {
  const buttons: RadioButtonWithLabelOption<string>[] = [
    {
      value: 'server-limit',
      label: <ManageUsersServerLimitOption {...props}/>,
      testId: 'user-quota-unlimited-radio-button',
      disabled
    },
    {
      value: 'site-user-quota',
      label: <ManageUsersSiteUserQuotaOption {...props}/>,
      testId: 'user-quota-enabled-radio-button',
      disabled
    }
  ];

  if(FeatureFlags.siteQuotaEnabled) {
    buttons.push({
      value: 'site-tiered-capacity',
      label: <ManageUsersSiteRoleQuotaOption {...props}/>,
      testId: 'site-tiered-quota-enabled-radio-button',
      disabled
    });
  }

  return buttons;
}

export interface LicenseMaxCounts {
  userCountMax: number;
  tierCreatorCountMax: number;
  tierExplorerCountMax: number;
  tierViewerCountMax: number;
}

export function useLicensingInfoInfo() {
  const [licenseMaxCounts, setLicenseMaxCounts] = React.useState<LicenseMaxCounts>(null);

  React.useEffect(() => {
    if (!licenseMaxCounts) {
      fetch().then((licensingInfo: ILicensingInfoWithTotals) => {
        if (licensingInfo) {
          const counts = calculateUserMaxCounts(licensingInfo);
          setLicenseMaxCounts(counts);
        }
      });
    }
  }, [licenseMaxCounts]);

  return licenseMaxCounts;
}

export function calculateUserMaxCounts(licensingInfo: ILicensingInfoWithTotals): LicenseMaxCounts {
  let tierCreatorCountMax = licensingInfo.totalCreatorUsers ?? 0;
  let tierExplorerCountMax = licensingInfo.totalExplorerUsers ?? 0;
  let tierViewerCountMax = licensingInfo.totalViewerUsers ?? 0;
  let userCountMax = tierCreatorCountMax + tierExplorerCountMax + tierViewerCountMax;

  const noLicenses = !licensingInfo.productKeyInfos || !licensingInfo.productKeyInfos.length;
  if (licensingInfo.coreLicensingInfos || noLicenses) {
    tierExplorerCountMax = NumberInputRange.INT.MAX;
    tierViewerCountMax = NumberInputRange.INT.MAX;
    userCountMax = NumberInputRange.INT.MAX;
  }

  return {
    userCountMax,
    tierCreatorCountMax,
    tierExplorerCountMax,
    tierViewerCountMax
  };
}
